
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

import { API } from "../api";
import ToastMessage from "../components/ToastMessage";

const UploadContext = createContext(null);

const ACTIVE_STATUSES = [
  "preparing",
  "compressing",
  "uploading",
  "cancelling",
];

export const UploadProvider = ({ children }) => {
  const [uploads, setUploads] = useState([]);

  const controllers = useRef(new Map());
  const successTimer = useRef(null);
  const pendingSuccess = useRef(false);

  const updateUpload = useCallback((id, changes) => {
    setUploads((items) =>
      items.map((item) =>
        item.id === id ? { ...item, ...changes } : item
      )
    );
  }, []);

  const startUpload = useCallback(
    ({ formData, name = "New post", prepare = null }) => {
      // Prevent an older success timer from hiding a new upload.
      if (successTimer.current) {
        clearTimeout(successTimer.current);
        successTimer.current = null;
      }

      pendingSuccess.current = false;

      const id = crypto.randomUUID();
      const controller = new AbortController();

      controllers.current.set(id, controller);

      setUploads((items) => [
        {
          id,
          name,
          progress: 0,
          status: prepare ? "preparing" : "uploading",
          error: null,
        },
        ...items,
      ]);

      (async () => {
        let registered = false;

        try {
          // Optional background preparation (e.g. video compression).
          if (prepare) {
            updateUpload(id, {
              status: "preparing",
              progress: 0,
            });

            const preparedData = await prepare({
              formData,
              signal: controller.signal,

              setStatus: (status) => {
                if (!controller.signal.aborted) {
                  updateUpload(id, { status });
                }
              },

              setProgress: (progress) => {
                if (!controller.signal.aborted) {
                  updateUpload(id, { progress });
                }
              },
            });

            if (controller.signal.aborted) {
              updateUpload(id, { status: "cancelled" });
              return;
            }

            if (preparedData instanceof FormData) {
              formData = preparedData;
            }
          }

          if (controller.signal.aborted) {
            updateUpload(id, { status: "cancelled" });
            return;
          }

          updateUpload(id, {
            status: "uploading",
            progress: 0,
          });

          // Register the upload with the backend.
          await API.post("/posts/uploads/start", {
            uploadId: id,
          });

          registered = true;

          if (controller.signal.aborted) {
            const result = await API.post(
              `/posts/uploads/${id}/cancel`
            );

            updateUpload(id, {
              status:
                result.data.status === "cancelled"
                  ? "cancelled"
                  : "failed",
            });

            return;
          }

          // Upload the prepared media.
          await API.post("/posts", formData, {
            signal: controller.signal,

            headers: {
              "X-Upload-ID": id,
            },

            onUploadProgress: (event) => {
              if (event.total && !controller.signal.aborted) {
                updateUpload(id, {
                  progress: Math.min(
                    99,
                    Math.round(
                      (event.loaded / event.total) * 100
                    )
                  ),
                });
              }
            },
          });

          updateUpload(id, {
            status: "completed",
            progress: 100,
            error: null,
          });
        } catch (error) {
          if (controller.signal.aborted) {
            if (!registered) {
              updateUpload(id, {
                status: "cancelled",
              });

              return;
            }

            try {
              const result = await API.post(
                `/posts/uploads/${id}/cancel`
              );

              if (result.data.status === "cancelled") {
                updateUpload(id, {
                  status: "cancelled",
                });
              } else {
                updateUpload(id, {
                  status: "failed",
                  error: "Unable to confirm cancellation.",
                });
              }
            } catch (cancelError) {
              const tooLate =
                cancelError.response?.status === 409 &&
                cancelError.response?.data?.status === "too_late";

              updateUpload(id, {
                status: tooLate ? "processing" : "failed",
                error: tooLate
                  ? "Publication already started. Check your feed."
                  : "Cancellation could not be confirmed. Check your feed.",
              });
            }
          } else {
            updateUpload(id, {
              status: "failed",
              error:
                error.response?.data?.message ||
                error.message ||
                "Upload failed",
            });
          }
        } finally {
          controllers.current.delete(id);
        }
      })();

      return id;
    },
    [updateUpload]
  );

  const cancelUpload = useCallback(
    (id) => {
      const controller = controllers.current.get(id);

      if (!controller) return;

      updateUpload(id, {
        status: "cancelling",
      });

      controller.abort();
    },
    [updateUpload]
  );

  const removeUpload = useCallback((id) => {
    setUploads((items) =>
      items.filter(
        (item) =>
          item.id !== id ||
          ![
            "completed",
            "failed",
            "cancelled",
            "processing",
          ].includes(item.status)
      )
    );
  }, []);

  // Automatically dismiss successful uploads and show a toast.
  useEffect(() => {
    if (successTimer.current) {
      clearTimeout(successTimer.current);
      successTimer.current = null;
    }

    const hasActiveUploads = uploads.some((upload) =>
      ACTIVE_STATUSES.includes(upload.status)
    );

    if (hasActiveUploads) {
      pendingSuccess.current = true;
      return;
    }

    if (!uploads.length) {
      pendingSuccess.current = false;
      return;
    }

    // Wait until a batch of uploads has actually finished.
    if (!pendingSuccess.current) return;

    const allSuccessful = uploads.every(
      (upload) => upload.status === "completed"
    );

    if (!allSuccessful) {
      pendingSuccess.current = false;
      return;
    }

    const completedIds = uploads.map((upload) => upload.id);
    const completedCount = completedIds.length;

    successTimer.current = setTimeout(() => {
      pendingSuccess.current = false;
      successTimer.current = null;

      // Remove only uploads belonging to this completed batch.
      setUploads((items) =>
        items.filter(
          (item) =>
            !completedIds.includes(item.id) ||
            item.status !== "completed"
        )
      );

      ToastMessage(
        completedCount === 1
          ? "Your post has been uploaded successfully!"
          : "All your posts have been uploaded successfully!"
      );
    }, 1500);

    return () => {
      if (successTimer.current) {
        clearTimeout(successTimer.current);
        successTimer.current = null;
      }
    };
  }, [uploads]);

  return (
    <UploadContext.Provider
      value={{
        uploads,
        startUpload,
        cancelUpload,
        removeUpload,
      }}
    >
      {children}
    </UploadContext.Provider>
  );
};

export const useUploads = () => {
  const context = useContext(UploadContext);

  if (!context) {
    throw new Error(
      "useUploads must be used inside UploadProvider"
    );
  }

  return context;
};
